import { useCallback, useState, useContext, useEffect, useRef } from 'react';
import { PostMetaContext, SiteContext, PostContentContext } from '@immediate_media/pocket-config';
import { LOGIN_ROUTE } from '@immediate_media/auth-constants';
import { useCurrentUser, useLoggedIn } from '@immediate_media/auth-lib-react';
import useFabricApi from './use-fabric-api';
import { pushToDataLayer } from './utils/data-layer-push';
import { SEGMENT_EVENT_NAMES } from './constants';
const useSavedItem = (item = null) => {
  const [currentUser] = useCurrentUser();
  const isLoggedIn = useLoggedIn();
  const {
    clientId,
    source,
    entity
  } = useContext(PostMetaContext);
  const siteContext = useContext(SiteContext);
  const {
    layout,
    postId,
    postType,
    title
  } = useContext(PostContentContext);
  const fabricApi = useFabricApi();
  const [savedItem, setSavedItem] = useState(item);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const mounted = useRef(false);
  const getBaseData = useCallback(() => {
    const data = {
      client: siteContext?.siteKey,
      clientId,
      source,
      entity,
      userId: currentUser?.guid,
      userVanityRef: currentUser?.vanityId
    };

    // The collections API expects strings for all of these fields; the conversion
    // here is primarily in case one of the IDs is returned as an integer
    return Object.keys(data).reduce((acc, key) => {
      if (data[key]) {
        acc[key] = data[key].toString();
      }
      return acc;
    }, {});
  }, [siteContext?.siteKey, clientId, source, entity, currentUser?.guid, currentUser?.vanityId]);
  const save = async () => {
    const createdItem = await fabricApi.collections.savedItems.create(getBaseData());
    setSavedItem(createdItem);
    pushToDataLayer({
      event: 'content_save',
      item_id: postId,
      item_name: title,
      content_type: postType,
      feature: 'save',
      component: 'Button',
      placement: 'PostHeader',
      placement_variant: layout
    });
    window.dispatchEvent(new Event(SEGMENT_EVENT_NAMES.itemSaved));
  };
  const onMount = useCallback(async () => {
    const loadedItem = await fabricApi.collections.savedItems.find(getBaseData());
    setSavedItem(loadedItem);

    // TODO: Uncomment for FAB-26053
    // if (!item && (new URL(window.location.href)).searchParams.get('save')) {
    //   await save();
    // }

    setLoading(false);
    mounted.current = true;
  }, [fabricApi.collections.savedItems, getBaseData]);
  useEffect(() => {
    if (mounted.current) {
      return;
    }
    if (isLoggedIn && currentUser && !savedItem) {
      onMount();
    }
  }, [isLoggedIn, currentUser, onMount, savedItem]);
  const remove = async () => {
    await fabricApi.collections.savedItems.delete(savedItem.id);
    setSavedItem(null);
  };

  // TODO: Save the recipe if unsaved on return after sign in, see FAB-26053
  // The code was implemented as I was in here doing save button things anyway
  // but the ticket moved out again as considered not a priority.
  // Just need to uncomment some lines below and add tests. Should work!
  const redirectToLogin = () => {
    // const currentUrl = new URL(window.location.href);
    const loginUrl = new URL(LOGIN_ROUTE, window.location.href);

    // currentUrl.searchParams.set('save', 1);
    // loginUrl.searchParams.set('redirect', currentUrl.href);

    window.location.assign(loginUrl.href);
  };
  const toggleSavedItem = async () => {
    if (loading) {
      return;
    }
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }
    setLoading(true);
    setError(false);
    try {
      await (savedItem ? remove : save)();
    } catch (err) {
      setLoading(false);

      // The access and refresh tokens have probably expired
      if (err.response?.status === 401) {
        redirectToLogin();
        return;
      }
      setError(true);
      throw err;
    }
    setLoading(false);
  };
  return [savedItem, toggleSavedItem, loading, error];
};
export default useSavedItem;